import { useCallback } from 'react';
import { commitLocalUpdate, useRelayEnvironment } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import RelayDataID from '@atlassian/relay-data-id';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';

type UseUpdateIssueTypeLinksOnCreateParams = {
	issueTypeId: string;
	projectId: string;
};

/**
 * Hook that updates the issueTypes connection for additional fields when a new issue type is created.
 * Adds the new issue type edge to the connection with the required properties.
 */
export const useUpdateIssueTypeLinksOnCreate = () => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();

	return useCallback(
		({ issueTypeId, projectId }: UseUpdateIssueTypeLinksOnCreateParams) => {
			commitLocalUpdate(environment, (store) => {
				const issueRelayId = RelayDataID({ id: issueTypeId }, 'JiraIssueType');
				const issueTypeRecordToLink = issueRelayId ? store.get(issueRelayId) : null;

				const ari = JiraProjectAri.create({
					siteId: cloudId,
					projectId,
				});

				const linkedProjectRelayId = RelayDataID({ id: ari }, 'JiraProject');

				if (issueTypeRecordToLink && linkedProjectRelayId) {
					const linkedProjectRecord = store.get(linkedProjectRelayId);

					if (linkedProjectRecord) {
						let connection = ConnectionHandler.getConnection(linkedProjectRecord, 'issueTypes');

						if (!connection) {
							const connectionId = `client:${linkedProjectRelayId}:issueTypes`;
							connection = store.get(connectionId);
						}

						if (connection) {
							const newIssueTypeRecord = ConnectionHandler.createEdge(
								store,
								connection,
								issueTypeRecordToLink,
								'JiraIssueTypeEdge',
							);
							newIssueTypeRecord.setValue(true, 'isDueDatePresentInIssueCreate');
							newIssueTypeRecord.setValue(true, 'isAssigneePresentInIssueCreate');
							ConnectionHandler.insertEdgeBefore(connection, newIssueTypeRecord);
						}
					}
				}
			});
		},
		[environment, cloudId],
	);
};
